import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, ShieldAlert } from "lucide-react";
import { adminEndpoints, triggerBlobDownload } from "@/lib/adminApi";
import {
    AdminPageHeader,
    AdminCard,
    StatusPill,
} from "@/components/admin/AdminUI";

const DOC_LABEL = {
    cv: "CV",
    e_visa: "e-Visa",
    passport: "Passport",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function bytesToKB(bytes) {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function AdminApplicationDetailPage() {
    const { id } = useParams();
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingDoc, setDownloadingDoc] = useState(null);
    const [savingStatus, setSavingStatus] = useState(null);

    const load = () => {
        setLoading(true);
        adminEndpoints
            .application(id)
            .then((d) => setApp(d))
            .catch((err) =>
                setError(err?.response?.data?.detail || "Failed to load application"),
            )
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const download = async (docType) => {
        setDownloadingDoc(docType);
        try {
            const { blob, filename } = await adminEndpoints.downloadDocument(id, docType);
            triggerBlobDownload(blob, filename);
        } catch (err) {
            toast.error(err?.response?.data?.detail || `Could not download ${docType}`);
        } finally {
            setDownloadingDoc(null);
        }
    };

    const changeStatus = async (nextStatus) => {
        setSavingStatus(nextStatus);
        try {
            const updated = await adminEndpoints.applicationStatus(id, nextStatus);
            setApp(updated);
            toast.success(`Status changed to ${nextStatus}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update status");
        } finally {
            setSavingStatus(null);
        }
    };

    return (
        <div data-testid="admin-application-detail-page">
            <div className="mb-4">
                <Link
                    to="/admin/applications"
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70 hover:text-brand-gold"
                    data-testid="admin-application-back"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to applications
                </Link>
            </div>

            {loading ? (
                <AdminCard>Loading application…</AdminCard>
            ) : error ? (
                <p className="border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            ) : app ? (
                <>
                    <AdminPageHeader
                        eyebrow={`Reference · ${app.reference_number}`}
                        title={app.full_name}
                        description={`Applied for ${app.job_title_snapshot} · submitted ${formatDate(
                            app.submitted_at,
                        )}`}
                        testId="admin-application-header"
                        actions={<StatusPill status={app.status} />}
                    />

                    <div
                        className="mb-8 flex items-start gap-3 border-l-4 border-brand-gold bg-brand-gold/10 p-4"
                        data-testid="admin-application-pii-warning"
                    >
                        <ShieldAlert className="h-5 w-5 flex-shrink-0 text-brand-gold" />
                        <p className="font-body text-sm text-brand-offwhite">
                            <strong className="font-mono text-brand-gold">Contains sensitive personal data.</strong>{" "}
                            Handle in line with UK GDPR. Downloaded files must be stored securely and
                            deleted from local devices once the recruitment decision is made.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        {/* Left: applicant info */}
                        <div className="md:col-span-2 space-y-6">
                            <AdminCard testId="admin-application-fields">
                                <h3 className="font-display text-xl uppercase tracking-wide text-brand-gold">
                                    Applicant details
                                </h3>
                                <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <DetailRow label="Email" value={app.email} mono />
                                    <DetailRow label="Phone" value={app.phone} mono />
                                    <DetailRow label="Nationality" value={app.nationality} />
                                    <DetailRow label="Current country" value={app.current_country} />
                                    <DetailRow label="Right to work" value={app.right_to_work} />
                                    <DetailRow
                                        label="Years experience"
                                        value={`${app.years_experience} years`}
                                    />
                                </dl>
                            </AdminCard>
                            <AdminCard testId="admin-application-cover-letter">
                                <h3 className="font-display text-xl uppercase tracking-wide text-brand-gold">
                                    Cover letter
                                </h3>
                                <p className="mt-4 whitespace-pre-wrap font-body text-sm leading-relaxed text-brand-offwhite/90">
                                    {app.cover_letter}
                                </p>
                            </AdminCard>
                        </div>

                        {/* Right: docs + actions */}
                        <div className="space-y-6">
                            <AdminCard testId="admin-application-documents">
                                <h3 className="font-display text-xl uppercase tracking-wide text-brand-gold">
                                    Documents
                                </h3>
                                <ul className="mt-4 space-y-2">
                                    {["cv", "e_visa", "passport"].map((k) => {
                                        const meta = app.documents?.[k];
                                        if (!meta) return null;
                                        return (
                                            <li
                                                key={k}
                                                className="flex flex-col gap-2 border border-white/10 bg-white/[0.02] p-3"
                                            >
                                                <div>
                                                    <p className="font-body text-sm text-brand-offwhite">
                                                        {DOC_LABEL[k]}
                                                    </p>
                                                    <p className="font-mono text-[10px] text-brand-offwhite/60">
                                                        {meta.original_name} · {bytesToKB(meta.size)}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => download(k)}
                                                    disabled={downloadingDoc === k}
                                                    className="inline-flex items-center justify-center gap-2 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold disabled:opacity-60"
                                                    data-testid={`admin-application-download-${k}`}
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                    {downloadingDoc === k ? "Downloading…" : "Download"}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </AdminCard>

                            <AdminCard testId="admin-application-status-actions">
                                <h3 className="font-display text-xl uppercase tracking-wide text-brand-gold">
                                    Actions
                                </h3>
                                <div className="mt-4 flex flex-col gap-2">
                                    <button
                                        type="button"
                                        disabled={savingStatus === "shortlisted" || app.status === "shortlisted"}
                                        onClick={() => changeStatus("shortlisted")}
                                        className="border border-emerald-400 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 hover:bg-emerald-400 hover:text-brand-charcoal disabled:opacity-50"
                                        data-testid="admin-application-shortlist"
                                    >
                                        {savingStatus === "shortlisted" ? "Saving…" : "Shortlist"}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={savingStatus === "rejected" || app.status === "rejected"}
                                        onClick={() => changeStatus("rejected")}
                                        className="border border-rose-400 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-rose-400 hover:bg-rose-400 hover:text-brand-charcoal disabled:opacity-50"
                                        data-testid="admin-application-reject"
                                    >
                                        {savingStatus === "rejected" ? "Saving…" : "Reject"}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={savingStatus === "new" || app.status === "new"}
                                        onClick={() => changeStatus("new")}
                                        className="border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold disabled:opacity-50"
                                        data-testid="admin-application-reset"
                                    >
                                        {savingStatus === "new" ? "Saving…" : "Reset to new"}
                                    </button>
                                </div>
                            </AdminCard>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
}

function DetailRow({ label, value, mono }) {
    return (
        <div>
            <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                {label}
            </dt>
            <dd
                className={`mt-1 text-sm ${
                    mono ? "font-mono text-brand-offwhite" : "font-body text-brand-offwhite"
                }`}
            >
                {value || "—"}
            </dd>
        </div>
    );
}
